// Lightweight fetch wrapper for the template editor.
//
// Centralizes CSRF + JSON headers + endpoint URL substitution so the
// Vue components stay focused on UI.

export function makeApi({ csrfToken, endpoints }) {
  const baseHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-CSRF-Token': csrfToken
  }

  function url(template, vars) {
    return Object.entries(vars).reduce(
      (out, [k, v]) => out.replace(`:${k}`, encodeURIComponent(v)),
      template
    )
  }

  async function send(method, target, body) {
    const res = await fetch(target, {
      method,
      credentials: 'same-origin',
      headers: baseHeaders,
      body: body == null ? undefined : JSON.stringify(body)
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || data.success === false) {
      const message = data.error || data.errors?.join(', ') || res.statusText
      const err = new Error(message)
      err.status = res.status
      err.details = data
      throw err
    }
    return data
  }

  return {
    updateSection(blockId, payload) {
      return send('PATCH', url(endpoints.update_section, { block_id: blockId }), payload)
    },
    addItem(blockId, item = {}) {
      return send('POST', url(endpoints.add_item, { block_id: blockId }), { item })
    },
    removeItem(blockId, index) {
      return send('DELETE', url(endpoints.remove_item, { block_id: blockId, index }))
    },
    removeItemByUid(blockId, uid) {
      return send('DELETE', url(endpoints.remove_item_by_uid, { block_id: blockId, uid }))
    },
    reorderItems(blockId, orderedUids) {
      return send('POST', url(endpoints.reorder_items, { block_id: blockId }), { ordered_uids: orderedUids })
    },
    updateOverrides(blockId, overrides, replace = false) {
      return send('PATCH', url(endpoints.update_overrides, { block_id: blockId }), { element_overrides: overrides, replace })
    },
    listVersions(blockId) {
      return send('GET', url(endpoints.section_versions, { block_id: blockId }))
    },
    restoreVersion(blockId, versionId) {
      return send('POST', url(endpoints.restore_version, { block_id: blockId, version_id: versionId }))
    },
    reorderSections(pageId, blockIds) {
      return send('POST', url(endpoints.reorder_sections, { page_id: pageId }), { block_ids: blockIds })
    },
    duplicateSection(blockId) {
      return send('POST', url(endpoints.duplicate_section, { block_id: blockId }))
    },
    deleteSection(blockId) {
      return send('DELETE', url(endpoints.delete_section, { block_id: blockId }))
    },
    addSection(pageId, sectionId) {
      return send('POST', url(endpoints.add_section, { page_id: pageId }), { site_template_section_id: sectionId })
    },
    updateBrand(payload) {
      return send('PATCH', endpoints.update_brand, payload)
    },
    updateBlog(payload) {
      return send('PATCH', endpoints.update_blog, payload)
    },
    updateChat(payload) {
      return send('PATCH', endpoints.update_chat, { chat: payload })
    },
    updateBar(payload) {
      return send('PATCH', endpoints.update_bar, { bar: payload })
    },
    updatePopup(payload) {
      return send('PATCH', endpoints.update_popup, { popup: payload })
    },
    updateFooter(payload) {
      return send('PATCH', endpoints.update_footer, { footer: payload })
    },
    updateHeader(payload) {
      return send('PATCH', endpoints.update_header, { header: payload })
    },
    updateSeoSettings(payload) {
      return send('PATCH', endpoints.update_seo_settings, { seo: payload })
    },
    addPage(payload) {
      return send('POST', endpoints.add_page, payload)
    },
    deletePage(pageId) {
      return send('DELETE', url(endpoints.delete_page, { page_id: pageId }))
    },
    generatePageImages(pageId) {
      return send('POST', url(endpoints.generate_page_images, { page_id: pageId }))
    },
    generateImage(blockId, field, arrayIndex) {
      return send('POST', url(endpoints.generate_image, { block_id: blockId }), { field, array_index: arrayIndex })
    },
    pageContent(pageId) {
      return send('GET', url(endpoints.page_content, { page_id: pageId }))
    },
    updatePageSeo(pageId, seo) {
      return send('PATCH', url(endpoints.update_page_seo, { page_id: pageId }), { seo })
    },
    updateFormFields(formId, fields) {
      return send('PATCH', url(endpoints.update_form_fields, { form_id: formId }), { fields })
    },
    updateFormSettings(formId, settings, extra = {}) {
      return send('PATCH', url(endpoints.update_form_settings, { form_id: formId }), { settings, ...extra })
    },
    uploadImage(file, alt) {
      const form = new FormData()
      form.append('file', file)
      if (alt) form.append('alt', alt)
      return fetch(endpoints.upload_image, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
        body: form
      }).then(async(res) => {
        const data = await res.json().catch(() => ({}))
        if (!res.ok || data.success === false) {
          throw new Error(data.error || 'upload failed')
        }
        return data
      })
    },
    uploadChatVideo(file) {
      const form = new FormData()
      form.append('file', file)
      return fetch(endpoints.chat_video, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
        body: form
      }).then(async(res) => {
        const data = await res.json().catch(() => ({}))
        if (!res.ok || data.success === false) {
          throw new Error(data.error || 'upload failed')
        }
        return data
      })
    },
    deleteChatVideo() {
      return send('DELETE', endpoints.chat_video)
    }
  }
}
